import { AnimatePresence, motion } from "framer-motion";
import "./ClassificacaoPontos.css";
import { useProfile } from "../../../../contexts/ProfileContext.tsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import MotionSemAutorizacao from "../../../../core/components/MotionSemAutorizacao.tsx";
import NavbarV3 from "../../../../components/NavbarV3/NavbarV3.tsx";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent.tsx";
import {
	primeiroDiaDosMesPassadoFormatado,
	ultimoDiaDosMesPassadoFormatado,
} from "../../../../core/utils/UtilityDates.ts";
import {
	buscarRelatorioKm,
	BuscarRelatorioKmProps,
	RelatorioKm,
} from "../../../../services/ModuloGerenciador/Motorista.ts";
import useService from "../../../../hooks/useService.ts";
import { useToast } from "../../../../contexts/ToastContext.tsx";
import TabelaClassificacaoPontos from "./components/TabelaClassificacaoPontos.tsx";
import ClassificacaoPontosHeader from "./components/ClassificacaoPontosHeader.tsx";

function ClassificacaoPontos() {
	const { profile, hasRegra } = useProfile();
	const { showToast } = useToast();

	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [valuesPesquisa, setValuesPesquisa] = useState<BuscarRelatorioKmProps>({
		token: profile?.token || "",
		dataInicio: primeiroDiaDosMesPassadoFormatado,
		dataFinal: ultimoDiaDosMesPassadoFormatado,
		pontoKm: 1,
		pontoViagem: 1200,
	});
	const [classificacaoPontos, setClassificacaoPontos] = useState<{
		motoristas: RelatorioKm[];
		proprietarios: RelatorioKm[];
	}>({
		motoristas: [],
		proprietarios: [],
	});

	const [, buscarClassificacao] = useService(
		() => buscarRelatorioKm(valuesPesquisa),
		{
			autoStart: false,
			onData: (response) => {
				setClassificacaoPontos({
					motoristas: response.motoristas || [],
					proprietarios: response.proprietarios || [],
				});
			},
			onError: () => {
				showToast("Erro ao carregar classificação de pontos", "error");
			},
		}
	);

	const emptyValuesPesquisa = useMemo(() => {
		return {
			dataInicio: primeiroDiaDosMesPassadoFormatado,
			dataFinal: ultimoDiaDosMesPassadoFormatado,
			pontoKm: 1,
			pontoViagem: 1200,
		};
	}, [primeiroDiaDosMesPassadoFormatado, ultimoDiaDosMesPassadoFormatado]);

	const handleClearSearch = useCallback(() => {
		setValuesPesquisa({
			...emptyValuesPesquisa,
			token: profile?.token || "",
		});
		setClassificacaoPontos({
			motoristas: [],
			proprietarios: [],
		});
	}, [profile?.token]);

	useEffect(() => {
		if (profile?.token) {
			setSemAutorizacao(!hasRegra("Classificacao de Pontos"));
			setValuesPesquisa((prev) => ({
				...prev,
				token: profile?.token || "",
			}));
		}
	}, [profile?.token]);

	const csvListProprietarios = useMemo(() => {
		return classificacaoPontos.proprietarios.map((item) => ({
			classificacao: item.classificacao,
			nome: item.nome,
			documento: item.documento,
			tipo: item.tipo,
			kmFeitos: item.kmFeitos,
			viagensFeitas: item.viagensFeitas,
			pontos: item.pontos,
			placa: item.placa,
		}));
	}, [classificacaoPontos]);

	const csvListMotoristas = useMemo(() => {
		return classificacaoPontos.motoristas.map((item) => ({
			classificacao: item.classificacao,
			nome: item.nome,
			documento: item.documento,
			tipo: item.tipo,
			kmFeitos: item.kmFeitos,
			viagensFeitas: item.viagensFeitas,
			pontos: item.pontos,
			placa: item.placa,
		}));
	}, [classificacaoPontos]);

	const isLoading = useMemo(() => {
		return !profile?.token;
	}, [profile?.token]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.2 }}
			className="classificacao-pontos-container"
		>
			<NavbarV3 title="Classificação de Pontos" />
			<ClassificacaoPontosHeader
				valuesPesquisa={valuesPesquisa}
				setValuesPesquisa={setValuesPesquisa}
				handleSearch={() => buscarClassificacao()}
				clearSearch={handleClearSearch}
			/>
			<div className="classificacao-pontos-content-wrapper">
				<AnimatePresence mode="wait">
					{isLoading && <LoadingComponent key="loading-classificacao" />}
					{!isLoading && (
						<div
							key="classificacao-pontos-content"
							className="classificacao-pontos-content"
						>
							<TabelaClassificacaoPontos
								title="Proprietário"
								dados={classificacaoPontos.proprietarios}
								classificacaoPontosList={csvListProprietarios}
							/>
							<TabelaClassificacaoPontos
								title="Motorista"
								dados={classificacaoPontos.motoristas}
								classificacaoPontosList={csvListMotoristas}
							/>
						</div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
}

export default ClassificacaoPontos;
